import type { BadgeTone } from "../components/index.js";
import type { PlanStatus } from "../types.js";

/**
 * Mapa de status do plano → rótulo pt-BR + tom de badge (Design.md §5.4 / §7.1).
 * O `PlanStatus` do frontend usa os status do fluxo SP-API (DRAFT…BOX_LABELS_READY
 * + FAILED/CANCELLED); este mapa cobre TODOS — `Record<PlanStatus, …>` garante
 * exaustividade em tempo de compilação. Tons seguem §3.2/§10: azul = em
 * andamento, verde = concluído, cinza = neutro/cancelado, âmbar = falha.
 */
export interface PlanBadge {
  label: string;
  tone: BadgeTone;
}

export const PLAN_STATUS_BADGE: Record<PlanStatus, PlanBadge> = {
  DRAFT: { label: "Rascunho", tone: "gray" },
  REGISTERED_ON_AMAZON: { label: "Registrado na Amazon", tone: "blue" },
  PREP_DETAILS_SET: { label: "Prep Details definidos", tone: "blue" },
  PACKING_GENERATED: { label: "Opções de embalagem geradas", tone: "blue" },
  PACKING_CONFIRMED: { label: "Embalagem confirmada", tone: "blue" },
  PACKING_INFO_SET: { label: "Informações de embalagem enviadas", tone: "blue" },
  PLACEMENT_GENERATED: { label: "Opções de alocação geradas", tone: "blue" },
  TRANSPORT_OPTIONS_READY: { label: "Opções de transporte prontas", tone: "blue" },
  PLACEMENT_CONFIRMED: { label: "Alocação confirmada", tone: "blue" },
  PREP_COMPLETE: { label: "Preparação concluída", tone: "blue" },
  FNSKU_LABELS_READY: { label: "Etiquetas FNSKU prontas", tone: "blue" },
  BOX_LABELS_READY: { label: "Concluído", tone: "green" },
  FAILED: { label: "Falhou", tone: "amber" },
  CANCELLED: { label: "Cancelado", tone: "gray" }
};

export function planBadge(status: PlanStatus): PlanBadge {
  return PLAN_STATUS_BADGE[status];
}

/**
 * Status de plano em que o `setPackingInformation` JÁ foi aceito pela Amazon —
 * isto é, o Passo 2 (Método e embalagem) está COMPLETO e o plano avançou para a
 * geração de alocação. Fonte de verdade para habilitar "Avançar" no Passo 2:
 * a prontidão NÃO pode depender só do callback transitório do modal (que pode
 * não disparar se o modal fechar, o Cloud Tasks demorar, ou a página recarregar).
 */
const PACKING_INFO_DONE: ReadonlySet<PlanStatus> = new Set<PlanStatus>([
  "PACKING_INFO_SET",
  "PLACEMENT_GENERATED",
  "TRANSPORT_OPTIONS_READY",
  "PLACEMENT_CONFIRMED",
  "PREP_COMPLETE",
  "FNSKU_LABELS_READY",
  "BOX_LABELS_READY"
]);

/** Passo 2 concluído: setPackingInformation aceito pela Amazon (planStatus). */
export function isPackingInfoDone(status: PlanStatus): boolean {
  return PACKING_INFO_DONE.has(status);
}

/**
 * Método de embalagem confirmado (ou além): o plano passou da escolha de packing.
 * Usado para hidratar o estado de UI ao revisitar um plano já adiantado.
 */
export function isPackingConfirmedOrBeyond(status: PlanStatus): boolean {
  return status === "PACKING_CONFIRMED" || PACKING_INFO_DONE.has(status);
}

/** Status de workflow terminais: o plano não pode mais avançar pela UI. */
const TERMINAL_WORKFLOW_STATUSES: ReadonlySet<string> = new Set([
  "FAILED",
  "TIMED_OUT",
  "CANCELLED"
]);

export function isTerminalWorkflowStatus(workflowStatus: string): boolean {
  return TERMINAL_WORKFLOW_STATUSES.has(workflowStatus);
}
